import { motion, useReducedMotion } from "framer-motion";

// ─── Marketplace Visual Polish — Loading, Brand Color Cleanup & Provider
// Logos V1, Parte A ──────────────────────────────────────────────────────
// Replaces the previous winged-sandal/route illustration (dark navy
// backdrop, heavy gold accents throughout — exactly what Parte B asks to
// remove) with a lightweight orbital-ring loader: two concentric CSS-
// animated rings (Tailwind's own animate-spin, one reversed via a plain
// inline animationDirection override — no new keyframes, no new
// dependency) on a light, frosted backdrop consistent with the rest of
// this sprint's marketplace redesign, so the loading moment never flashes
// dark against an otherwise all-light marketplace.
//
// Behavioral contract preserved byte-for-byte from before (see
// coldStartVisibility.test.ts's own "the exiting full-screen loader
// cannot intercept marketplace clicks" guard): `if (!visible) return
// null` as the ONLY exit mechanism, never an exit-animation wrapper —
// an infinite CSS/motion animation on an exiting node would otherwise
// keep this fixed, full-screen overlay mounted (and clickable) past the
// moment `visible` goes false.

interface RheaLoaderProps {
  visible:  boolean;
  message?: string;
  /** Optional second line under `message` — this sprint's own suggested
   *  copy ("Comparando proveedores disponibles para este destino.") is
   *  the default, but never hardcoded as unconditional: a caller with a
   *  more specific context can override it, and passing an empty string
   *  hides the line entirely rather than rendering it blank. */
  subMessage?: string;
}

export function RheaLoader({
  visible,
  message = "Buscando experiencias reales...",
  subMessage = "Comparando proveedores disponibles para este destino.",
}: RheaLoaderProps) {
  const reduce = useReducedMotion();

  // Infinite nested animations can keep an exit node mounted after it becomes
  // transparent. Unmount immediately so results are interactive without delay.
  if (!visible) return null;

  return (
    <motion.div
      key="rhea-loader"
      data-testid="rhea-loader"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-6"
      style={{
        background:           "rgba(255,255,255,0.96)",
        backdropFilter:       "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.22 }}
      role="status"
      aria-live="polite"
      aria-label="Cargando"
    >
      {/* ── Orbital ring loader ── */}
      <div className="relative mb-6" style={{ width: "72px", height: "72px" }}>
        {/* Static track */}
        <div
          className="absolute inset-0 rounded-full"
          style={{ border: "3px solid #EAEAEA" }}
          aria-hidden
        />
        {/* Outer animated arc — magenta, clockwise */}
        <div
          className={reduce ? "absolute inset-0 rounded-full" : "absolute inset-0 rounded-full animate-spin"}
          style={{
            border:           "3px solid transparent",
            borderTopColor:   "#E8194B",
            borderRightColor: "#E8194B",
            animationDuration: "1.05s",
          }}
          aria-hidden
        />
        {/* Inner animated arc — smaller, reversed, softer magenta —
            the "orbital" second body this sprint's brief asks for. */}
        <div
          className={reduce ? "absolute rounded-full" : "absolute rounded-full animate-spin"}
          style={{
            top: "14px", left: "14px", right: "14px", bottom: "14px",
            border:            "3px solid transparent",
            borderBottomColor: "#F3AABE",
            borderLeftColor:   "#F3AABE",
            animationDuration:  "1.6s",
            animationDirection: "reverse",
          }}
          aria-hidden
        />
      </div>

      {/* Status message */}
      <motion.p
        className="font-semibold text-center"
        style={{ fontSize: "14px", color: "#111111" }}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          reduce
            ? { duration: 0 }
            : { duration: 0.45, delay: 0.12 }
        }
      >
        {message}
      </motion.p>

      {subMessage && (
        <motion.p
          className="text-center mt-1.5"
          style={{ fontSize: "12.5px", color: "#5A5A5A", maxWidth: "320px" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={
            reduce
              ? { duration: 0 }
              : { duration: 0.45, delay: 0.22 }
          }
        >
          {subMessage}
        </motion.p>
      )}

      {/* Wordmark */}
      <motion.p
        className="mt-5 tracking-[0.28em] uppercase"
        style={{ fontSize: "10px", color: "#5A5A5A", opacity: 0.55 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={
          reduce
            ? { duration: 0 }
            : { duration: 0.5, delay: 0.32 }
        }
      >
        Sift Trip
      </motion.p>
    </motion.div>
  );
}
